import { useMemo, useRef } from 'react';
import { Formik, FormikProps } from 'formik';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import ButtonLoading from '@mui/lab/LoadingButton';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import { CubeDefinitionRowItem } from 'types/api/SystemTenant/AROCube/CubeBuilderTypes';

const ModalContentStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  maxWidth: 'calc(100vw - 32px)',
  bgcolor: 'background.paper',
  borderRadius: 1,
  py: 2,
};

const textInputStyles = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    bgcolor: '#fff',
  },
};

const closeIconStyle = { color: '#98A2AE', cursor: 'pointer' };

export interface ModalCubeAttributeDisplayFormatProps {
  visible?: boolean;
  onClose?: () => void;
  cubeDefinitionItem: CubeDefinitionRowItem | null;
  onSubmitParameter?: (parameterValue: CubeDefinitionRowItem) => void;
}

type FormValues = {
  prefix: string;
  suffix: string;
};

export function ModalCubeAttributeDisplayFormat(props: ModalCubeAttributeDisplayFormatProps) {
  const formikRef = useRef<FormikProps<FormValues>>(null);
  const { onClose, visible = false, onSubmitParameter, cubeDefinitionItem } = props;

  const formikInitialValues = useMemo(
    () => ({
      prefix: cubeDefinitionItem?.displayFormat?.prefix ?? '',
      suffix: cubeDefinitionItem?.displayFormat?.suffix ?? '',
    }),
    [cubeDefinitionItem]
  );

  const handleOnSave = (data: FormValues) => {
    if (cubeDefinitionItem && onSubmitParameter) {
      onSubmitParameter({
        ...cubeDefinitionItem,
        displayFormat:
          data.prefix || data.suffix
            ? {
                prefix: data.prefix,
                suffix: data.suffix,
              }
            : null,
      });
    }
    onClose?.();
  };

  return (
    <Modal open={visible} onClose={onClose} aria-labelledby='cube-attribute-display-format-title'>
      <Stack direction='column' sx={ModalContentStyle}>
        <Stack px={2} justifyContent='space-between' direction='row' alignItems='center'>
          <Typography
            id='cube-attribute-display-format-title'
            variant='body1'
            component='h2'
            fontWeight='bold'
            sx={{ color: '#3B4797' }}
          >
            {`Display Format for ${cubeDefinitionItem?.attribute?.label ?? 'Cube Attribute'}`}
          </Typography>
          <IconButton onClick={onClose} aria-label='Close display format modal'>
            <HighlightOffRoundedIcon sx={closeIconStyle} />
          </IconButton>
        </Stack>

        <Divider sx={{ mt: 1 }} />

        <Formik innerRef={formikRef} enableReinitialize initialValues={formikInitialValues} onSubmit={handleOnSave}>
          {({ handleSubmit, handleChange, handleBlur, values }) => (
            <>
              <Stack spacing={2.5} px={3} py={3} sx={{ bgcolor: '#FBFBFB' }}>
                <TextField
                  label='Prefix'
                  value={values.prefix}
                  name='prefix'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  sx={textInputStyles}
                  size='small'
                  placeholder='e.g. $ '
                  type='text'
                  helperText='Displayed immediately before the value. Spaces are preserved.'
                />
                <TextField
                  label='Suffix'
                  value={values.suffix}
                  name='suffix'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  sx={textInputStyles}
                  size='small'
                  placeholder='e.g.  per KM'
                  type='text'
                  helperText='Displayed immediately after the value. Spaces are preserved.'
                />
                <Typography variant='body2' sx={{ color: '#5F6B7A' }}>
                  Preview: <strong>{`${values.prefix}1,234.56${values.suffix}`}</strong>
                </Typography>
              </Stack>

              <Stack px={2}>
                <Divider sx={{ mb: 1 }} />
                <Stack direction='row' justifyContent='flex-end' spacing={2} alignItems='center' sx={{ py: 1 }}>
                  <Button variant='main-table-panel-border' onClick={onClose}>
                    Cancel
                  </Button>
                  <ButtonLoading variant='main-table-panel' onClick={() => handleSubmit()}>
                    Save
                  </ButtonLoading>
                </Stack>
              </Stack>
            </>
          )}
        </Formik>
      </Stack>
    </Modal>
  );
}
